import { EmailTemplate } from '@spike/email-marketing-model';
import {
    FullWindowModal,
    FullWindowModalBody,
    FullWindowModalCloseButton,
    FullWindowModalHeader
} from 'components/UI/V3/Modals/FullWindowModal';
import clsx from 'clsx';
import { FunctionComponent, useState } from 'react';

import styles from './EmailTemplatePreviewModal.module.scss';

type PreviewDevice = 'desktop' | 'mobile';

interface EmailTemplatePreviewModalProps {
    /**
     * Template to preview. `null` hides the modal entirely — the parent owns
     * visibility through its own state and clears it on close.
     */
    template: EmailTemplate | null;
    onClose: () => void;
}

/**
 * Full-window preview of a template, framed as a faux browser window so the
 * reviewer reads it the way a recipient would (Unlayer / Mailchimp pattern).
 * Used from both the standalone Templates gallery and the campaign wizard's
 * Template Gallery sub-tab.
 *
 * Viewing-only by design — there's no "Use this template" CTA inside the
 * modal. To commit, the user closes the preview (X in the header) and
 * clicks "Use Template" on the gallery card. Keeps the preview free of
 * decision pressure.
 */
const EmailTemplatePreviewModal: FunctionComponent<EmailTemplatePreviewModalProps> = ({ template, onClose }) => {
    const [device, setDevice] = useState<PreviewDevice>('desktop');

    if (!template) return null;

    return (
        <FullWindowModal>
            <FullWindowModalHeader>
                <div className={styles.headerLayout}>
                    <h2 className={styles.templateName}>{template.name}</h2>

                    <div className={styles.deviceToggle}>
                        <button
                            type="button"
                            className={clsx(styles.deviceButton, {
                                [styles.deviceButtonActive]: device === 'desktop'
                            })}
                            onClick={() => setDevice('desktop')}
                        >
                            Desktop
                        </button>
                        <button
                            type="button"
                            className={clsx(styles.deviceButton, {
                                [styles.deviceButtonActive]: device === 'mobile'
                            })}
                            onClick={() => setDevice('mobile')}
                        >
                            Mobile
                        </button>
                    </div>

                    <FullWindowModalCloseButton
                        onClick={onClose}
                        ariaLabel="Close preview"
                        className={styles.closeButton}
                    />
                </div>
            </FullWindowModalHeader>

            <FullWindowModalBody className={styles.body}>
                <div className={styles.previewStage}>
                    <div className={clsx(styles.previewWindow, styles[`previewWindow_${device}`])}>
                        <div
                            className={styles.browserChrome}
                            aria-hidden="true"
                        >
                            <span className={clsx(styles.dot, styles.dotRed)} />
                            <span className={clsx(styles.dot, styles.dotYellow)} />
                            <span className={clsx(styles.dot, styles.dotGreen)} />
                        </div>
                        <div className={styles.iframeWrapper}>
                            {template.html ? (
                                <iframe
                                    className={styles.iframe}
                                    title={template.name}
                                    srcDoc={template.html}
                                    sandbox=""
                                />
                            ) : (
                                <div className={styles.placeholder}>This template has no content yet.</div>
                            )}
                        </div>
                    </div>
                </div>
            </FullWindowModalBody>
        </FullWindowModal>
    );
};

export default EmailTemplatePreviewModal;
